import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import * as api from "./apiCalls";

// ============ QUERY KEYS ============
export const queryKeys = {
  allCountries : "allCountries",
  allCities : (id) => ["allCities", id],
  malls: "malls",
  mallById: (id) => ["mall", id],
  restaurants: "restaurants",
  restaurantById: (id) => ["restaurant", id],
  users: "users",
  userById: (id) => ["user", id],
  orders: "adminOrders",
  orderById: (id) => ["adminOrder", id],
  subscriptionsOverview: "subscriptionsOverview",
  promoCodes: "promoCodes",
  promoCodeById: (id) => ["promoCode", id],
};

export const useAllCountries = () => {
  return useQuery({
    queryKey: [queryKeys.allCountries],
    queryFn: api.getAllCountries,
  });
}

export const useCitiesByCountry = (countryId) => {
  return useQuery({
    queryKey: queryKeys.allCities(countryId),
    queryFn: () => api.getCitiesByCountry(countryId),
    enabled: !!countryId,
  });
}

// ============ MALLS HOOKS ============
export const useMalls = (params) => {
  return useQuery({
    queryKey: [queryKeys.malls, params],
    queryFn: () => api.getMalls(params),
  });
};

export const useMallById = (id) => {
  return useQuery({
    queryKey: queryKeys.mallById(id),
    queryFn: () => api.getMallById(id),
    enabled: !!id,
  });
};

export const useCreateMall = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.createMall,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.malls] });
    },
  });
};

export const useUpdateMall = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => api.updateMall(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.malls] });
      queryClient.invalidateQueries({ queryKey: queryKeys.mallById(id) });
    },
  });
};

export const useDeleteMall = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.deleteMall,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.malls] });
    },
  });
};

// ============ RESTAURANTS HOOKS ============
// Always enabled — passing no filter returns the full system-wide list.
// Backwards-compat: accepts a positional mallId string or a filter object.
//
// The system-wide endpoint /restaurants/all wraps results twice:
//   { message, data: { data: [...rows], total, page, limit, totalPages } }
// The legacy mall-specific endpoint returned the flat shape directly.
// Normalize both here so every consumer can safely do `result.data` and
// expect an array. Without this, every screen has to know which shape it's
// getting back — and crashes with `restaurantsList.map is not a function`
// the moment that assumption breaks.
export const useRestaurants = (filter, params) => {
  return useQuery({
    queryKey: [queryKeys.restaurants, filter, params],
    queryFn: async () => {
      const res = await api.getRestaurants(filter, params);
      // Case A — current /restaurants/all: { data: { data: [...], page, ... } }
      if (res && typeof res === "object" && res.data && Array.isArray(res.data.data)) {
        return res.data;
      }
      // Case B — legacy mall-specific endpoint: { data: [...], page, ... }
      if (res && typeof res === "object" && Array.isArray(res.data)) {
        return res;
      }
      // Case C — bare array
      if (Array.isArray(res)) {
        return { data: res, total: res.length, page: 1, totalPages: 1 };
      }
      // Loading / 401 / unexpected shape — never crash a consumer's .map.
      return { data: [], total: 0, page: 1, totalPages: 0 };
    },
  });
};

export const useRestaurantById = (id) => {
  return useQuery({
    queryKey: queryKeys.restaurantById(id),
    queryFn: () => api.getRestaurantById(id),
    enabled: !!id,
  });
};

export const useCreateRestaurant = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.createRestaurant,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.restaurants] });
    },
  });
};

export const useUpdateRestaurant = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => api.updateRestaurant(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.restaurants] });
      queryClient.invalidateQueries({ queryKey: queryKeys.restaurantById(id) });
    },
  });
};

export const useDeleteRestaurant = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.deleteRestaurant,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.restaurants] });
    },
  });
};

// PATCH /admin/restaurants/:id/block — flip RestaurantStatus.
// Invalidate the restaurants list so the new status badge + visibility take
// effect across the admin table without a manual refresh.
export const useSetRestaurantBlockStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isBlocked, reason }) =>
      api.setRestaurantBlockStatus(id, isBlocked, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.restaurants] });
    },
  });
};

// PATCH /admin/restaurants/:id/approval — set approvalStatus.
// Once admin approves, the restaurant becomes visible to customers in the
// user app (subject to subscription + not-blocked filters too).
export const useSetRestaurantApprovalStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, approvalStatus, reason }) =>
      api.setRestaurantApprovalStatus(id, approvalStatus, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.restaurants] });
    },
  });
};

// ============ USERS HOOKS ============
export const useActiveUsers = () => {
  return useQuery({
    queryKey: [queryKeys.users, 'active'],
    queryFn: api.getActiveUsers,
  });
};

export const useBlockedUsers = () => {
  return useQuery({
    queryKey: [queryKeys.users, 'blocked'],
    queryFn: api.getBlockedUsers,
  });
};

export const useUsers = (type = 'active') => {
  // type: 'active' | 'blocked'
  return useQuery({
    queryKey: [queryKeys.users, type],
    queryFn: type === 'blocked' ? api.getBlockedUsers : api.getActiveUsers,
  });
};

export const useActivateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => api.activateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.users, 'active'] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.users, 'blocked'] });
      // Mall admins are activated/blocked through this same endpoint, so
      // refresh their table too (no-op if that screen isn't mounted).
      queryClient.invalidateQueries({ queryKey: ['mallAdmins'] });
    },
  });
};

export const useBlockUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => api.blockUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.users, 'active'] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.users, 'blocked'] });
      // Mall admins are activated/blocked through this same endpoint, so
      // refresh their table too (no-op if that screen isn't mounted).
      queryClient.invalidateQueries({ queryKey: ['mallAdmins'] });
    },
  });
};

// ============ MALL ADMIN HOOKS (GAP-018, Super Admin only) ============
export const useMallAdmins = () => {
  return useQuery({
    queryKey: ['mallAdmins'],
    queryFn: api.getMallAdmins,
  });
};

export const useCreateMallAdmin = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.createMallAdmin(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mallAdmins'] });
    },
  });
};

export const useUserById = (id) => {
  return useQuery({
    queryKey: queryKeys.userById(id),
    queryFn: () => api.getUserById(id),
    enabled: !!id,
  });
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.users, 'active'] });
    },
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => api.updateUser(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.users, 'active'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.userById(id) });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.users, 'active'] });
    },
  });
};

// ============ ORDERS HOOKS ============
// The list refreshes every 30 seconds while open, and keeps showing the last
// page while the next one loads.
export const useAdminOrders = (params) => {
  return useQuery({
    queryKey: [queryKeys.orders, params],
    queryFn: () => api.getAdminOrders(params),
    placeholderData: keepPreviousData,
    refetchInterval: 30000,
  });
};

export const useAdminOrder = (id) => {
  return useQuery({
    queryKey: queryKeys.orderById(id),
    queryFn: () => api.getAdminOrder(id),
    enabled: !!id,
  });
};

// ============ PROMO CODES HOOKS ============
export const usePromoCodes = () => {
  return useQuery({
    queryKey: [queryKeys.promoCodes],
    queryFn: api.getPromoCodes,
  });
};

export const usePromoCodeById = (id) => {
  return useQuery({
    queryKey: queryKeys.promoCodeById(id),
    queryFn: () => api.getPromoCodeById(id),
    enabled: !!id,
  });
};

export const useCreatePromoCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.createPromoCode,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.promoCodes] });
    },
  });
};

export const useUpdatePromoCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => api.updatePromoCode(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.promoCodes] });
      queryClient.invalidateQueries({ queryKey: queryKeys.promoCodeById(id) });
    },
  });
};

export const useDeletePromoCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.deletePromoCode,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.promoCodes] });
    },
  });
};

// ============ AUTH HOOKS ============
export const useLogin = () => {
  return useMutation({
    mutationFn: api.loginAdmin,
  });
};

export const useChangePassword = () => {
  return useMutation({
    mutationFn: api.changePassword,
  });
};

export const useUpdateProfile = () => {
  return useMutation({
    mutationFn: api.updateAdminProfile,
  });
};

export const useShopCategories = () => {
  return useQuery({
    queryKey: ["shopCategories"],
    queryFn: () => api.getShopCategories(),
    staleTime: 5 * 60 * 1000,
  });
};

// Shops' category suggestions (super admin). The sidebar shows how many wait.
export const useCategoryRequests = (options = {}) => {
  return useQuery({
    queryKey: ["categoryRequests"],
    queryFn: () => api.getCategoryRequests(),
    ...options,
  });
};

const refreshCategories = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: ["categoryRequests"] });
  queryClient.invalidateQueries({ queryKey: ["shopCategories"] });
  queryClient.invalidateQueries({ queryKey: [queryKeys.restaurants] });
};

export const useAddCategoryRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name, group }) => api.addCategoryRequest(id, { name, ...(group ? { group } : {}) }),
    onSuccess: () => refreshCategories(queryClient),
  });
};

export const useDeclineCategoryRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.declineCategoryRequest(id),
    onSuccess: () => refreshCategories(queryClient),
  });
};

export const useCreateShopCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ name, group }) => api.createShopCategory({ name, group }),
    onSuccess: () => refreshCategories(queryClient),
  });
};

export const useRemoveShopCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.removeShopCategory(id),
    onSuccess: () => refreshCategories(queryClient),
  });
};

export const useGetMallCusines = (mallId) => {
  return useQuery({
    queryKey: ['mallCusines', mallId],
    queryFn: () => api.getMallCusines(mallId),
    enabled: !!mallId,
  });
}

export const useGetAnalyticsData = () => {
  return useQuery({
    queryKey: ['analyticsData'],
    queryFn: api.getAnalyticsData,
  });
}

export const useGetMallAnalytics = (mallId, period, startDate, endDate) => {
  return useQuery({
    // Only include date params in the key when actually provided, so the key
    // isn't polluted with `undefined` (BUG-016). Backend derives the range from
    // `period` when explicit dates are absent.
    queryKey: ['mallAnalytics', mallId, period, startDate ?? null, endDate ?? null],
    queryFn: () => api.getMallAnalytics(mallId, period, startDate, endDate),
    enabled: !!mallId,
  });
}

// ============ VENDOR SUBSCRIPTIONS (super admin) ============
export const useRestaurantSubscriptions = (restaurantId) => {
  return useQuery({
    queryKey: ["restaurantSubscriptions", restaurantId],
    queryFn: () => api.getRestaurantSubscriptions(restaurantId),
    enabled: !!restaurantId,
  });
};

export const useSubscriptionPlans = () => {
  return useQuery({
    queryKey: ["subscriptionPlans"],
    queryFn: api.getSubscriptionPlans,
  });
};

export const useSetSubscriptionEndDate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, endDate }) => api.setSubscriptionEndDate(id, endDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["restaurantSubscriptions"] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.subscriptionsOverview] });
    },
  });
};

export const useGrantSubscription = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.grantSubscription(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["restaurantSubscriptions"] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.subscriptionsOverview] });
    },
  });
};

export const useSubscriptionsOverview = (params) => {
  return useQuery({
    queryKey: [queryKeys.subscriptionsOverview, params],
    queryFn: () => api.getSubscriptionsOverview(params),
    placeholderData: keepPreviousData,
  });
};
